import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useGetChannelTypesQuery } from 'Services/api/channelTypesApi';
import {
  ArchiveIcon,
  Button,
  CrossIcon,
  Dialog,
  EditIcon,
  Heading,
  Icon,
  IconButton,
  IntentTypes,
  Menu,
  MoreIcon,
  Pane,
  PlayIcon,
  Popover,
  Position,
  SearchInput,
  StopIcon, Strong, SymbolCircleIcon,
  Table,
  Text,
  Tooltip,
  toaster,
} from 'evergreen-ui';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import {
  useChangeChannelInstanceMutation,
  useDeleteChannelMutation,
  useGetChannelInstanceQuery,
  useGetChannelsQuery,
} from 'Services/api/channelsApi';
import { ChannelInfo } from './Info';

type Channel = {
  id: number
  description: string
  channelTypeId: number
  isStarted?: boolean
}

type ChannelType = {
  id: number
  kod: string
  name: string
  description: string
}

type ChannelInstance = {
  id: number
  status: 'STARTED' | 'STOPPED'
}

export default function ChannelsTableList({ isChannelStatistic = false }: { isChannelStatistic?: boolean}) {
  const { data: channelTypes = [], isFetching: isLoadingChannelTypes } = useGetChannelTypesQuery(undefined);
  const { data: channels = [], isFetching: isLoadingChannels } = useGetChannelsQuery(undefined);
  const { data: channelsInstances = [], isFetching: isLoadingChannelInstances } = useGetChannelInstanceQuery(undefined);
  const [searchTextFilter, setSearchTextFilter] = React.useState('');
  const [selectedChannel, setSelectedChannel] = React.useState<Channel | null>(null);

  // проставляем статусы активкности каналам, чтобы не проставлять их при фильтре по тексту
  const preparedChannels = React.useMemo(() => {
    return channels.map((channel: Channel) => ({
      ...channel,
      isStarted: (channelsInstances as ChannelInstance[])
        .find((channelInstance: ChannelInstance) => channelInstance.id === channel.id)?.status === 'STARTED',
    }));
  }, [channels, channelsInstances]);

  const filteredChannelTypesWithChannels = React.useMemo(() => {
    const filterChannelsBySearchText = preparedChannels
      .filter((channel: Channel) => channel.description.toUpperCase().includes(searchTextFilter.toUpperCase()));

    return channelTypes.reduce((acc: {[key:string]: Channel[]}, channelType: ChannelType) => {
      const channelTypeChannels: Channel[] = filterChannelsBySearchText
        .filter((channel: Channel) => channel.channelTypeId === channelType.id);
      if (channelTypeChannels.length > 0) {
        acc[channelType.description] = channelTypeChannels;
      }
      return acc;
    }, {});
  }, [channelTypes, preparedChannels, searchTextFilter]);

  const onChangeSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchTextFilter(value);
  };

  if (isLoadingChannelTypes || isLoadingChannels || isLoadingChannelInstances) return <SuspenseSpinner />;

  return (
    <Pane display="grid" gridTemplateColumns={isChannelStatistic ? 'max-content 1fr' : '1fr'} overflow="hidden">
      <Pane
        display="grid"
        gridTemplateRows="max-content 1fr"
        overflow="hidden"
        paddingRight=".2rem"
        rowGap=".2rem"
        borderRight={isChannelStatistic ? true : '1px solid lightgray'}
      >
        <Pane
          display="grid"
          gridTemplateColumns={isChannelStatistic ? '1fr' : '1fr max-content'}
          columnGap=".5rem"
        >
          <SearchInput
            width="100%"
            placeholder="Введите название канала"
            onChange={onChangeSearchText}
            value={searchTextFilter}
          />
          {!isChannelStatistic && (
          <Button
            intent="success"
            appearance="primary"
            is={NavLink}
            to="new"
          >
            Добавить канал отправки
          </Button>
          )}
        </Pane>
        <ChannelsTable
          channelTypesWithChannels={filteredChannelTypesWithChannels}
          isChannelStatistic={isChannelStatistic}
          setSelectedChannel={isChannelStatistic ? setSelectedChannel : undefined}
        />
      </Pane>
      {isChannelStatistic && (
        <Content channel={selectedChannel} />
      )}
    </Pane>
  );
}

type ChannelProps = {
  channel: Channel
  channelStatistic?: boolean
  setSelectedChannel?: React.Dispatch<React.SetStateAction<Channel | null>> | undefined
}

export function Channel({
  channel,
  channelStatistic = false,
  setSelectedChannel = undefined,
}: ChannelProps) {
  const [changeChannelInstance, { isLoading: isChangeChannelInstance }] = useChangeChannelInstanceMutation();
  const [deleteChannelById, { isLoading: isDeleting }] = useDeleteChannelMutation();

  const [deleteDialogData, setDeleteDialogData] = React.useState<Channel | null>(null);

  const meta = React.useMemo(() => ({
    rowColor: channel.isStarted ? '#f1faf5' : '#fef6f6',
    title: channel.isStarted ? 'Остановить' : 'Запустить',
    icon: channel.isStarted ? StopIcon : PlayIcon,
    intent: channel.isStarted ? 'danger' : 'success',
  }), [channel.isStarted]);

  const toggleChannelControll = async () => {
    await changeChannelInstance({
      id: channel.id,
      method: channel.isStarted ? 'stop' : 'start',
    }).unwrap();
  };

  const remove = async () => {
    await deleteChannelById(deleteDialogData?.id).unwrap();
    toaster.success('Удалено');
  };

  const onSelectChannel = () => {
    if (setSelectedChannel) {
      setSelectedChannel(channel);
    }
  };

  return (
    <Table.Row
      onSelect={onSelectChannel}
      isSelectable
      backgroundColor="none"
      fontSize=".8rem"
      width="100%"
    >
      <Table.Cell paddingLeft=".5rem">
        <Tooltip
          position={Position.TOP}
          content={channel.isStarted ? 'Канал включен' : 'Канал выключен'}
        >
          <Icon
            icon={SymbolCircleIcon}
            color={channel.isStarted ? '#80daab' : '#ec9b9b'}
            size={20}
            padding={0}
            margin={0}
          />
        </Tooltip>
        <Text padding={8}>{channel.description}</Text>
      </Table.Cell>
      {
        !channelStatistic && (
          <Table.Cell justifyContent="flex-end">
            <IconButton
              width={40}
              title={meta.title}
              icon={meta.icon}
              appearance="minimal"
              intent={meta.intent as IntentTypes}
              onClick={toggleChannelControll}
              isLoading={isChangeChannelInstance}
            />
            <Popover
              position={Position.BOTTOM_LEFT}
              content={<PopoverMenu channel={channel} setDeleteDialogData={setDeleteDialogData} />}
            >
              <IconButton icon={MoreIcon} appearance="minimal" />
            </Popover>
          </Table.Cell>
        )
      }
      {
        deleteDialogData && (
          <Dialog
            isShown
            title="Внимание"
            shouldCloseOnOverlayClick={false}
            cancelLabel="Отмена"
            confirmLabel="Удалить"
            intent="danger"
            onCloseComplete={() => setDeleteDialogData(null)}
            onConfirm={remove}
            isConfirmLoading={isDeleting}
          >
            <Text>{`Удалить канал ${deleteDialogData.description}?`}</Text>
          </Dialog>
        )
      }
    </Table.Row>
  );
}

type ChannelsTableProps = {
  setSelectedChannel?: React.Dispatch<React.SetStateAction<Channel | null>> | undefined
  channelTypesWithChannels: {
    [key: string]: Channel[]
  }
  isChannelStatistic?:boolean
}

function ChannelsTable({
  setSelectedChannel = undefined,
  channelTypesWithChannels = {},
  isChannelStatistic = false,
}: ChannelsTableProps) {
  return (
    <Pane overflow="auto">
      {
      Object.entries(channelTypesWithChannels).map(([channelTypeDescription, channels]) => (
        <Pane key={channelTypeDescription} display="grid" gridTemplateRows="max-content 1fr" rowGap=".5rem">
          <Strong marginTop=".5rem">{channelTypeDescription}</Strong>
          <Pane>
            {
              React.Children.toArray(channels.map((channel) => (
                <Channel channel={channel} channelStatistic={isChannelStatistic} setSelectedChannel={setSelectedChannel} />
              )))
            }
          </Pane>
        </Pane>
      ))
    }
    </Pane>
  );
}

type PopoverMenuProps = {
  channel: Channel
  setDeleteDialogData: React.Dispatch<React.SetStateAction<Channel | null>>
}

function PopoverMenu({ channel, setDeleteDialogData }: PopoverMenuProps) {
  const navigate = useNavigate();
  const goToLogs = () => navigate('/settings/journals/channels-statistics');
  return (
    <Menu>
      <Menu.Group>
        <Menu.Item
          icon={ArchiveIcon}
          intent="none"
          onClick={goToLogs}
        >
          Логи
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          icon={EditIcon}
          is={Link}
          to={`${channel.id}`}
          onSelect={() => {
            // close();
          }}
        >
          Редактировать
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          icon={CrossIcon}
          intent="danger"
          onClick={() => {
            setDeleteDialogData(channel);
          }}
        >
          Удалить
        </Menu.Item>
      </Menu.Group>
    </Menu>
  );
}

function Content({ channel }: {channel: Channel | null}) {
  if (channel === null) {
    return (
      <Pane display="grid" justifyContent="center" alignContent="center">
        <Heading size={700}>&larr; Выберите канал слева</Heading>
      </Pane>
    );
  }

  return (
    <Pane padding=".5rem" display="grid">
      <ChannelInfo
        channel={channel}
      />
    </Pane>
  );
}
